import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarCheck, MailCheck, Clock3, Phone, Mail, MapPin } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { createLead, createConsultation } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

// Topic list aligned to the actual services on acumenfs.co.uk
const TOPICS = [
    "Mortgages & Property",
    "Protection & Insurance",
    "Business Loans & Funding",
    "Bridging & Short-Term Finance",
    "Growth & Expansion Planning",
    "General enquiry",
];

const TIMES = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export default function Contact() {
    const [params] = useSearchParams();
    const defaultTab = params.get("topic") === "consultation" ? "consult" : "message";
    const [tab, setTab] = useState(defaultTab);
    const { settings } = useSettings();
    const hasContact = !!(settings?.phone || settings?.email || settings?.address);

    return (
        <>
            <section className="pt-24 md:pt-32 pb-12">
                <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-semibold">
                                Get in touch
                            </span>
                        </div>
                        <h1
                            data-testid="contact-title"
                            className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl tracking-tight text-brand-primary leading-[1.02]"
                        >
                            Let's make
                            <br />
                            <em className="italic font-light text-brand-accent">
                                a plan.
                            </em>
                        </h1>
                    </div>
                    <div className="lg:col-span-5">
                        <p className="text-lg text-brand-muted leading-relaxed">
                            Share a few details and an adviser will be in touch to
                            understand your goals before offering any recommendation.
                        </p>
                    </div>
                </div>
            </section>

            <section className="pb-24">
                <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4 space-y-6">
                        {hasContact && (
                            <div className="bg-brand-surface border border-brand-border p-6 space-y-4">
                                <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
                                    Direct contact
                                </div>
                                {settings.phone && (
                                    <a href={`tel:${settings.phone}`} data-testid="contact-settings-phone" className="flex items-center gap-3 text-brand-primary hover:text-brand-accent transition-colors">
                                        <Phone size={16} className="text-brand-accent" />
                                        <span className="font-serif-editorial text-xl">{settings.phone}</span>
                                    </a>
                                )}
                                {settings.email && (
                                    <a href={`mailto:${settings.email}`} data-testid="contact-settings-email" className="flex items-center gap-3 text-brand-primary hover:text-brand-accent transition-colors">
                                        <Mail size={16} className="text-brand-accent" />
                                        <span className="font-serif-editorial text-xl">{settings.email}</span>
                                    </a>
                                )}
                                {settings.address && (
                                    <div data-testid="contact-settings-address" className="flex items-start gap-3 text-brand-primary">
                                        <MapPin size={16} className="text-brand-accent mt-1" />
                                        <span className="text-base whitespace-pre-line font-medium">{settings.address}</span>
                                    </div>
                                )}
                                {settings.office_hours && (
                                    <div className="text-sm text-brand-muted pt-2 border-t border-brand-border">
                                        {settings.office_hours}
                                    </div>
                                )}
                            </div>
                        )}
                        <InfoBlock
                            icon={MailCheck}
                            title="Every enquiry read by an adviser"
                            body="Use the form to tell us what you're trying to achieve — we'll pick it up personally."
                            testid="contact-info-enquiry"
                        />
                        <InfoBlock
                            icon={CalendarCheck}
                            title="Prefer a call?"
                            body="Request a consultation and we'll arrange a time that suits you."
                            testid="contact-info-consult"
                        />
                        <InfoBlock
                            icon={Clock3}
                            title="No pressure, no jargon"
                            body="We take the time to understand your circumstances before recommending anything."
                            testid="contact-info-noprs"
                        />

                        <div className="bg-brand-primary text-brand-bg p-8">
                            <div className="text-xs uppercase tracking-[0.22em] text-brand-accent mb-3">
                                Your goals
                            </div>
                            <p className="font-serif-editorial text-2xl leading-snug">
                                Our priority.
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        <div className="flex gap-1 mb-6 border-b border-brand-border">
                            <TabButton
                                active={tab === "message"}
                                onClick={() => setTab("message")}
                                testid="contact-tab-message"
                            >
                                Send a message
                            </TabButton>
                            <TabButton
                                active={tab === "consult"}
                                onClick={() => setTab("consult")}
                                testid="contact-tab-consult"
                            >
                                Request a consultation
                            </TabButton>
                        </div>

                        {tab === "message" ? <MessageForm /> : <ConsultForm />}
                    </div>
                </div>
            </section>
        </>
    );
}

function TabButton({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-6 py-4 text-sm tracking-wide transition-colors border-b-2 -mb-px ${
                active
                    ? "text-brand-primary border-brand-primary"
                    : "text-brand-muted border-transparent hover:text-brand-primary"
            }`}
        >
            {children}
        </button>
    );
}

function InfoBlock({ icon: Icon, title, body, testid }) {
    return (
        <div
            data-testid={testid}
            className="border-b border-brand-border pb-6"
        >
            <div className="flex items-center gap-3 mb-2 text-brand-accent">
                <Icon size={16} />
            </div>
            <div className="font-serif-editorial text-2xl text-brand-primary leading-tight">
                {title}
            </div>
            <div className="text-sm text-brand-muted mt-2">{body}</div>
        </div>
    );
}

function MessageForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.message) {
            toast.error("Please fill in name, email, phone and a short message.");
            return;
        }
        setLoading(true);
        try {
            await createLead({ ...form, source: "contact_page" });
            toast.success("Message received. An adviser will be in touch shortly.");
            navigate("/thank-you");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="contact-message-form"
            className="bg-brand-surface border border-brand-border p-8 md:p-10 space-y-6"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                    label="Full name"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    testid="contact-name"
                    required
                />
                <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    testid="contact-email"
                    required
                />
                <Field
                    label="Phone"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    testid="contact-phone"
                    required
                />
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">
                        Topic
                    </label>
                    <Select
                        value={form.service}
                        onValueChange={(v) => setForm({ ...form, service: v })}
                    >
                        <SelectTrigger
                            data-testid="contact-service"
                            className="mt-2 w-full border-0 border-b border-brand-border bg-transparent rounded-none px-0 py-3 text-lg text-brand-primary focus:ring-0 focus:border-brand-primary"
                        >
                            <SelectValue placeholder="Choose a topic" />
                        </SelectTrigger>
                        <SelectContent>
                            {TOPICS.map((t) => (
                                <SelectItem
                                    key={t}
                                    value={t}
                                    data-testid={`contact-topic-${t}`}
                                >
                                    {t}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div>
                <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">
                    How can we help?
                </label>
                <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message"
                    className="mt-2 w-full bg-transparent border-b border-brand-border py-3 text-lg text-brand-primary focus:border-brand-primary outline-none transition-colors resize-none"
                    placeholder="Tell us a little about what you're looking to do…"
                />
            </div>

            <div className="flex items-center justify-between pt-4">
                <p className="text-xs text-brand-muted max-w-sm leading-relaxed">
                    By submitting your details, you confirm you have read and
                    understood our{" "}
                    <a
                        href="/privacy"
                        className="underline text-brand-primary hover:text-brand-accent"
                    >
                        Privacy Policy
                    </a>
                    .
                </p>
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="bg-brand-primary text-brand-bg px-8 py-4 text-sm font-medium hover:bg-brand-primaryHover transition-colors disabled:opacity-60"
                >
                    {loading ? "Sending…" : "Send message"}
                </button>
            </div>
        </form>
    );
}

function ConsultForm() {
    const [date, setDate] = useState();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        time: "",
        topic: "",
        notes: "",
    });
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !date || !form.time) {
            toast.error("Please fill in name, email, phone, date and time.");
            return;
        }
        setLoading(true);
        try {
            await createConsultation({
                name: form.name,
                email: form.email,
                phone: form.phone,
                preferred_date: date.toISOString().split("T")[0],
                preferred_time: form.time,
                topic: form.topic || null,
                notes: form.notes || null,
            });
            toast.success("Request received — we'll confirm the slot shortly.");
            navigate("/thank-you");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="consult-form"
            className="bg-brand-surface border border-brand-border p-8 md:p-10 space-y-8"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                    <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">
                        Preferred date
                    </label>
                    <div className="mt-3 border border-brand-border p-2 inline-block">
                        <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={(d) =>
                                d < new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            data-testid="consult-calendar"
                        />
                    </div>
                </div>
                <div className="space-y-6">
                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">
                            Preferred time
                        </label>
                        <div className="mt-3 grid grid-cols-4 gap-2">
                            {TIMES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    data-testid={`consult-time-${t}`}
                                    onClick={() => setForm({ ...form, time: t })}
                                    className={`px-2 py-3 text-sm border transition-colors ${
                                        form.time === t
                                            ? "border-brand-primary bg-brand-primary text-brand-bg"
                                            : "border-brand-border text-brand-muted hover:border-brand-primary hover:text-brand-primary"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    <Field
                        label="Full name"
                        value={form.name}
                        onChange={(v) => setForm({ ...form, name: v })}
                        testid="consult-name"
                        required
                    />
                    <Field
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={(v) => setForm({ ...form, email: v })}
                        testid="consult-email"
                        required
                    />
                    <Field
                        label="Phone"
                        value={form.phone}
                        onChange={(v) => setForm({ ...form, phone: v })}
                        testid="consult-phone"
                        required
                    />
                    <Field
                        label="Topic (optional)"
                        value={form.topic}
                        onChange={(v) => setForm({ ...form, topic: v })}
                        testid="consult-topic"
                    />
                </div>
            </div>

            <div className="pt-4 border-t border-brand-border space-y-4">
                <p className="text-xs text-brand-muted leading-relaxed">
                    By submitting your details, you confirm you have read and
                    understood our{" "}
                    <a
                        href="/privacy"
                        className="underline text-brand-primary hover:text-brand-accent"
                    >
                        Privacy Policy
                    </a>
                    .
                </p>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-brand-muted text-sm">
                        <CalendarCheck size={16} className="text-brand-accent" />
                        <span>No obligation — we'll confirm availability by email.</span>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="consult-submit"
                        className="bg-brand-primary text-brand-bg px-8 py-4 text-sm font-medium hover:bg-brand-primaryHover transition-colors disabled:opacity-60"
                    >
                        {loading ? "Sending…" : "Request consultation"}
                    </button>
                </div>
            </div>
        </form>
    );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">
                {label}
                {required && <span className="text-brand-accent ml-1">*</span>}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                required={required}
                className="mt-2 w-full bg-transparent border-b border-brand-border py-3 text-lg text-brand-primary focus:border-brand-primary outline-none transition-colors"
            />
        </div>
    );
}
